import React, { useState } from 'react';
import { Agency } from '../../core/_models';
import { useAxios } from '../../core/_axios';
import { useAuth } from '../../AuthContext';

export default function ManageAgencyAdmin({
    agency,
    setAgency,
}: {
    agency: Agency;
    setAgency: (agency: Agency) => void;
}) {
    const axios = useAxios();
    const { currentUser } = useAuth();
    const [updating, setUpdating] = useState(false);
    const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);

    const handlePaymentStatusToggle = async () => {
        if (updating || !currentUser?.is_super_super_user) return;

        setUpdating(true);
        setUpdateSuccess(null);
        setUpdateError(null);

        try {
            const response = await axios.post(
                `/api/v1/app/authenticated/admin/agencies/${agency.unique_identifier}/update-payment-status`,
                {
                    payment_out_of_date: !agency.payment_out_of_date
                }
            );

            if (response.data.data?.agency) {
                const updatedAgency = new Agency(response.data.data.agency);
                setAgency(updatedAgency);
                setUpdateSuccess(
                    updatedAgency.payment_out_of_date
                        ? 'Agency marked as payment out of date'
                        : 'Agency marked as payment up to date'
                );
            } else {
                setUpdateError('Failed to update payment status');
            }
        } catch (error) {
            console.error('Error updating payment status:', error);
            setUpdateError('An error occurred while updating payment status');
        } finally {
            setUpdating(false);
        }
    };

    if (!currentUser?.is_super_super_user) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                <span className="block sm:inline">
                    Only super administrators can access this section.
                </span>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-5">
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Agency Payment Status</h3>
                </div>
                <div className="card-body">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900">
                                Payment Out of Date
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                                When enabled, accounts belonging to this agency will see a warning banner
                                and may have restricted access to creating new services.
                            </p>
                            <div className="mt-2">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                    agency.payment_out_of_date
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-green-100 text-green-800'
                                }`}>
                                    {agency.payment_out_of_date ? 'Payment Out of Date' : 'Payment Up to Date'}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agency.payment_out_of_date}
                                    onChange={handlePaymentStatusToggle}
                                    disabled={updating}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
                            </label>
                        </div>
                    </div>

                    {updateSuccess && (
                        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                            {updateSuccess}
                        </div>
                    )}

                    {updateError && (
                        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {updateError}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
